import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from 'store/store';

import { isFrictionlessLoginEligibleBL } from 'businessLogic/Auth/Auth';
import { emailRegex, internationalPhoneRegex } from 'shared/freeTextModalValidators';
import { isExternalPaymentMethodSelectedSelector } from 'store/payment/selectors';

type AuthSelectorState = Pick<RootState, 'auth'>;

export const authTicketSelector = (store: AuthSelectorState): string | undefined => {
  return store.auth.ticket;
};

export const isFrictionlessLoginEnabledSelector = (store: AuthSelectorState): boolean => {
  return store.auth.isFrictionlessLoginEnabled;
};

export const isFrictionlessLoginEligibleSelector = (
  store: Pick<RootState, 'auth' | 'featureFlags' | 'sale' | 'insight'>
): boolean => {
  return isFrictionlessLoginEligibleBL({
    frictionlessLoginEmail: store.auth.frictionlessLoginEmail,
    frictionlessLoginSessionId: store.auth.frictionlessLoginSessionId,
    featureFlags: store.featureFlags,
    sale: store.sale,
    insight: store.insight,
  });
};

export const frictionlessLoginEmailSelector = (store: AuthSelectorState): string | undefined => {
  return store.auth.frictionlessLoginEmail;
};

export const frictionlessLoginPreviouslySavedEmailSelector = (
  store: AuthSelectorState
): string | undefined => {
  return store.auth?.frictionlessLoginPreviouslySavedEmail;
};

export const frictionlessLoginSessionIdSelector = (
  store: AuthSelectorState
): string | undefined => {
  return store.auth.frictionlessLoginSessionId;
};

export const frictionlessCheckoutSelector = createSelector(
  (state: RootState) => state.auth?.frictionlessCheckout,
  isExternalPaymentMethodSelectedSelector,
  (frictionlessCheckoutData, isExternalPayment) => {
    return {
      ...frictionlessCheckoutData,
      isSelected: frictionlessCheckoutData?.isSelected && !isExternalPayment,
    };
  }
);

export const isFrictionlessCheckoutSelectedSelector = createSelector(
  frictionlessCheckoutSelector,
  (frictionlessCheckout) => Boolean(frictionlessCheckout?.isSelected)
);

export const isFrictionlessCheckoutDetailsRequiredSelector = (
  store: AuthSelectorState
): boolean => {
  return store.auth?.frictionlessCheckout?.isDetailsRequired;
};

export const isUserClosedFrictionlessOTPSelector = (
  store: AuthSelectorState
): boolean | undefined => {
  return store.auth?.frictionlessCheckout?.isUserClosedOTP;
};

export const isFrictionlessCheckoutCompletedSelector = (
  store: AuthSelectorState
): boolean | undefined => {
  return store.auth?.frictionlessCheckout?.isCompleted;
};

export const isFrictionlessCheckoutWalletCreatedSelector = (
  store: AuthSelectorState
): boolean | undefined => {
  return store.auth?.frictionlessCheckout?.isWalletCreated;
};

export const frictionlessCheckoutEmailSelector = (store: AuthSelectorState): string => {
  return store.auth?.frictionlessCheckout?.email || '';
};

export const shouldShowIdentitySignInWidgetSelector = (store: AuthSelectorState): boolean => {
  return store.auth?.frictionlessLoginPhase3?.shouldShowIdentitySignInWidget ?? false;
};

export const shouldShowSignInCTASelector = (store: AuthSelectorState): boolean => {
  return store.auth?.frictionlessLoginPhase3?.shouldShowSignInCTA ?? false;
};

export const isSignInCTADecisionPendingSelector = (store: AuthSelectorState): boolean => {
  return store.auth?.frictionlessLoginPhase3?.isSignInCTADecisionPending ?? true;
};

export const isIdentitySignInWidgetErrorSelector = (store: AuthSelectorState): boolean => {
  if (!store.auth?.frictionlessLoginPhase3) {
    return false;
  }
  return store.auth.frictionlessLoginPhase3.isIdentitySignInWidgetError ?? false;
};

export const isIdentitySignInWidgetCompletedSelector = (store: AuthSelectorState): boolean => {
  if (!store.auth?.frictionlessLoginPhase3) {
    return false;
  }
  return store.auth.frictionlessLoginPhase3.isIdentitySignInWidgetCompleted ?? false;
};

export const frictionlessCheckoutContrySelector = createSelector(
  frictionlessCheckoutSelector,
  (frictionlessData) =>
    frictionlessData?.countryCode || {
      n: 'United States of America',
      a2: 'US',
      a3: 'USA',
      dial_code: '+1',
    }
);

export const isFrictionlessCheckoutEmailValidSelector = createSelector(
  frictionlessCheckoutEmailSelector,
  (email) => Boolean(email && emailRegex.test(email))
);

export const frictionlessCheckoutPhoneSelector = (store: AuthSelectorState): string => {
  return store.auth?.frictionlessCheckout?.phone || '';
};

export const isFrictionlessCheckoutDetailsModalClosedSelector = (
  store: AuthSelectorState
): boolean | undefined => {
  return store.auth?.frictionlessCheckout?.isDetailsModalClosed;
};

export const isFrictionlessCheckoutPhoneValidSelector = createSelector(
  frictionlessCheckoutPhoneSelector,
  (phone) => Boolean(phone && internationalPhoneRegex.test(phone))
);

export const isUserAlreadySignedUpSelector = (store: AuthSelectorState): boolean =>
  Boolean(store.auth?.frictionlessCheckout?.isUserAlreadySignedUp);

export const isFrictionlessCheckoutDataInvalidSelector = createSelector(
  isFrictionlessCheckoutSelectedSelector,
  isFrictionlessCheckoutPhoneValidSelector,
  isFrictionlessCheckoutEmailValidSelector,
  (isSelected, isPhoneValid, isEmailValid) => {
    return isSelected && (!isPhoneValid || !isEmailValid);
  }
);

export const isFrictionlessUserNameAvailableSelector = (store: AuthSelectorState): boolean => {
  return store.auth?.frictionlessCheckout?.isUserNameAvailable ?? false;
};

export const isFrictionlessRegistrationRequiredSelector = (store: AuthSelectorState): boolean => {
  return store.auth?.frictionlessCheckout?.isRegistrationRequired ?? false;
};

export const isGenerateFactorLoadingSelector = (state: RootState) =>
  state.auth.isGenerateFactorLoading || false;

export const isFrictionlessIdentityErrorSelector = (store: AuthSelectorState): boolean => {
  return store.auth?.frictionlessCheckout?.showIdentityError || false;
};

export const frictionlessIdentityErrorMessageSelector = (store: AuthSelectorState): string => {
  return store.auth?.frictionlessCheckout?.identityErrorMessage || '';
};
